﻿/* https://brandplatform.smartphoto.com/general/popups/ai-multiple-filters/ */
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { showLoadingDialog } from "./loading-dialog";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
import { createPopup } from "../technical/popup-new";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	hasClass,
} from "../../common/html";
import { IPhoto, PhotoSourcesPopup } from "../organisms/photo-sources-popup";
import { updateParameter } from "../../utils/querystring";
import { initDisableContextMenu } from "../technical/disable-context-menu";

export interface IAiMultipleFiltersDependencies {
	close?: () => void;
	error?: (message: string) => void;
	selectedFilterQuery?: string;
}

export function createAiMultipleFilters(
	deps: IAiMultipleFiltersDependencies,
	pvc: string,
	design: string,
	theme?: string,
) {
	const buttonDisabledClass = "a-button--disabled";
	const eventDisposables: IDisposable = compositeDisposable(undefined);
	let hasCurrentCreations = false;
	let attemptsLeft = 0;
	const activePollers: NodeJS.Timeout[] = [];

	const swapPhoto = (popupEl: HTMLElement, photoId: string, photoUrl: string) => {
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
		const emptyStateEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-emptystate", false);
		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
		const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
		mainPhoto.src = photoUrl;
		mainPhoto.removeAttribute("data-pendingid");
		expressRemoveClass(mainPhoto, "u-hide");
		expressAddClass(emptyStateEl, "u-hide");

		continueButtonEls.forEach((continueButton) => {
			const currentHref = continueButton.getAttribute("href") || continueButton.dataset.href;
			let updatedHref = updateParameter("photoids", photoId, currentHref);

			// Add all the history photos as a selection to the user
			const selectionIds = expressQuerySelectorAll<HTMLElement>(
				popupEl,
				".technical-aifilter-history[data-photoid]:not(.technical-aifilter-history-template)",
			).map((x) => x.dataset.photoid);
			updatedHref = updateParameter("selectionids", selectionIds.join(","), updatedHref);
			if (theme) updatedHref = updateParameter("theme", theme, updatedHref);

			continueButton.setAttribute("href", updatedHref);
			continueButton.removeAttribute("data-href");
			continueButton.classList.remove(buttonDisabledClass);
		});

		loading.classList.add("u-hide");
	};

	// This method shows an error because something went wrong
	const showError = (
		popupEl: HTMLElement,
		pageLogComponent: IPageLog,
		pendingId: string,
		errorMessage: string,
		filterNumber: string,
		attemptsText?: string,
	) => {
		if (!errorMessage) throw new Error("pendingError called without errorMessage");
		const matchingFilter = expressQuerySelector<HTMLElement>(
			popupEl,
			`.technical-aifilter-filter[data-filternumber='${filterNumber}']`,
			false,
		);
		const matchingFilterTitleEl =
			matchingFilter && expressQuerySelector<HTMLElement>(matchingFilter, ".technical-filter-name", false);

		// reset main photo
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
		if (mainPhoto.dataset.pendingid == pendingId || mainPhoto.dataset.pendingid === "null") {
			mainPhoto.removeAttribute("data-pendingid");
			const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
			loading.classList.add("u-hide");
		}

		// reset history
		const historyEl = expressQuerySelector<HTMLImageElement>(
			popupEl,
			`.technical-aifilter-history[data-filternumber="${filterNumber}"]`,
			false,
		);
		historyEl && historyEl.remove();

		// reset selected filter
		const selectedFilter = expressQuerySelector(
			popupEl,
			`.technical-aifilter-filter[data-filternumber="${filterNumber}"]`,
			false,
		);
		selectedFilter && selectedFilter.classList.remove("p-ai-multiple-filters__filter-container--active");

		// set pagelog
		pageLogComponent.updateMessageAndStyle(
			PageLogStyle.Error,
			errorMessage.replace("{0}", matchingFilterTitleEl?.innerText || ""),
			false,
		);

		// updatePendingLabels(attemptsText);
	};

	// This method will poll the server for a pending photo in the history
	const pollForPending = async (popupEl: HTMLElement, pageLogComponent: IPageLog, pendingId: string) => {
		fetch("/apicore/aifilter/aipoll", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ id: pendingId }),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data && data.status === "done") {
					// When done, show the photo in the history
					const historyEl = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-partnerid='${pendingId}']`,
						false,
					);
					if (historyEl) {
						const loadingEl = expressQuerySelector<HTMLElement>(historyEl, ".technical-photo-loading");
						const historyImage = expressQuerySelector<HTMLImageElement>(historyEl, "img", true);
						historyImage.classList.remove("u-hide");
						historyImage.src = data.url;
						historyEl.dataset.photoid = data.id;
						historyEl.dataset.photourl = data.url;
						loadingEl.classList.add("u-hide");
					}

					// If the main photo is waiting for this pending, swap it + change the CTA URL
					const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
					if (mainPhoto.dataset.pendingid == pendingId) swapPhoto(popupEl, data.id, data.url);

					const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);
					if (attemptsLeft === 0) {
						expressAddClass(uploadButton, buttonDisabledClass);
					} else {
						expressRemoveClass(uploadButton, buttonDisabledClass);
					}
				} else if (data && data.errorMessage) {
					const matchingHistoryPhoto = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-photoid='${pendingId}']`,
						false,
					);
					showError(
						popupEl,
						pageLogComponent,
						pendingId,
						data.errorMessage,
						matchingHistoryPhoto?.dataset.filternumber,
						data.attemptsLabel,
					);
				} else {
					activePollers.push(
						setTimeout(() => {
							pollForPending(popupEl, pageLogComponent, pendingId);
						}, 1500),
					);
				}
			});
	};

	const init = async () => {
		const aiPhotoPopup = await showLoadingDialog(
			createPopup({
				dialogId: "showaiphotopopup_" + pvc + "_" + design,
				view: "PhotoWithAiFilter",
				closeOnClickOutsidePopup: false,
				onClose: () => {
					// Stop all active pollers
					activePollers.forEach((poller) => clearTimeout(poller));
					deps.close();
				},
				onBeforeOpen: (popupEl: HTMLElement) => {
					// Set attempts left (attribute on the popup)
					attemptsLeft = parseInt(popupEl.dataset.attemptsLeft) || 0;

					// Load the main picture if it is pending
					const pageLog = expressQuerySelector<HTMLElement>(popupEl, ".technical-top-message-log", true);
					const pageLogComponent = createPageLog(pageLog);
					const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
					const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
					const emptyStateEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-emptystate", false);
					const attemptsLabelEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-attempts", false);
					const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
					const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);
					const activeFilterClass = "p-ai-multiple-filters__filter-container--active";
					const activeHistoryClass = "p-ai-multiple-filters__history-container--active";

					enum ContinueButtonLabel {
						Continue,
						SelectFilter,
						SelectGenerated,
					}

					if (mainPhoto) {
						let activePhotoId = mainPhoto.dataset.originalPhotoId;
						let activePhotoUrl = mainPhoto.src;

						// Add upload button functionality
						const initPhotoUpload = () => {
							if (hasClass(uploadButton, buttonDisabledClass)) return;
							pageLogComponent.hideLog(); // reset page log

							const onImportPhotosFinished = async (photos: IPhoto[]): Promise<void> => {
								if (!photos || photos.length === 0) {
									return;
								}
								mainPhoto.src = photos[0].mediumUrl;
								mainPhoto.dataset.originalPhotoId = photos[0].fileId;
								expressRemoveClass(mainPhoto, "u-hide");
								expressAddClass(emptyStateEl, "u-hide");
								const disabledAiFilters = expressQuerySelectorAll<HTMLElement>(
									popupEl,
									".p-ai-multiple-filters__container-forbidden",
								);
								disabledAiFilters.forEach((filter) =>
									expressRemoveClass(filter, "p-ai-multiple-filters__container-forbidden"),
								);
								enableFilters();
								activePhotoId = mainPhoto.dataset.originalPhotoId;
								activePhotoUrl = mainPhoto.src;
								uploadButton.textContent = uploadButton.dataset.labeluploadanother;
								updateContinueButtonLabels(ContinueButtonLabel.SelectFilter);
								deselectActiveFiltersAndHistory();
							};

							// disable the button
							expressAddClass(uploadButton, buttonDisabledClass);

							const photoSources = new PhotoSourcesPopup(onImportPhotosFinished, 1);
							photoSources.openPhotoStoresPopup({
								onBeforeClose: () => {
									expressRemoveClass(uploadButton, buttonDisabledClass);
								},
							});
						};

						if (uploadButton) {
							eventDisposables.add(expressEventListener(uploadButton, "click", () => initPhotoUpload()));
						}

						// Enable the history clicks
						const historyContainer = expressQuerySelector<HTMLElement>(
							popupEl,
							".technical-aifilter-history-container",
							false,
						);
						if (historyContainer) {
							// Add polling for the pending images
							const pendingHistory = expressQuerySelectorAll<HTMLLinkElement>(
								historyContainer,
								".technical-aifilter-history[data-partnerid]",
							);
							pendingHistory.forEach((historyItem) => {
								const pendingId = historyItem.dataset.partnerid;
								pollForPending(popupEl, pageLogComponent, pendingId);
							});

							// Add click to swap the foto to the one from the history
							eventDisposables.add(
								expressEventListener(historyContainer, "click", (e) => {
									const historyDiv = e.target.closest(".technical-aifilter-history");
									if (historyDiv && historyDiv.dataset.photoid) {
										swapPhoto(popupEl, historyDiv.dataset.photoid, historyDiv.dataset.photourl);
										deselectActiveHistory();

										// Select the clicked history as being active
										historyDiv.classList.add(activeHistoryClass);

										// Match the clicked history with the clicked filter
										deselectActiveFilters();
										const matchingFilter = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-filter[data-filterNumber='" + historyDiv.dataset.filternumber + "']",
											false,
										);
										if (matchingFilter && historyDiv.dataset.originalphotoid === mainPhoto.dataset.originalPhotoId)
											matchingFilter.classList.add(activeFilterClass);

										// Add a divider between past creations and current ones
										if (hasCurrentCreations) {
											const divider = expressQuerySelector<HTMLImageElement>(
												popupEl,
												".technical-aifilter-history-divider",
												false,
											);
											if (divider) divider.classList.remove("u-hide");
										}

										updateContinueButtonLabels(ContinueButtonLabel.Continue);
									}
								}),
							);
						}

						const deselectActiveFilters = () => {
							const activeFilters = expressQuerySelectorAll<HTMLElement>(
								popupEl,
								".technical-aifilter-filter." + activeFilterClass,
							);
							activeFilters.forEach((activeFilter) => activeFilter.classList.remove(activeFilterClass));
						};

						const deselectActiveHistory = () => {
							const activeHistory = expressQuerySelectorAll<HTMLElement>(
								popupEl,
								".technical-aifilter-history." + activeHistoryClass,
							);
							activeHistory.forEach((activeHistory) => activeHistory.classList.remove(activeHistoryClass));
						};

						const deselectActiveFiltersAndHistory = () => {
							deselectActiveFilters();
							deselectActiveHistory();
						};

						const updateContinueButtonLabels = (label: ContinueButtonLabel) => {
							// Update the label of the buttons
							continueButtonEls.forEach((continueButton) => {
								if (label === ContinueButtonLabel.Continue)
									continueButton.textContent = continueButton.dataset.labelcontinue;
								if (label === ContinueButtonLabel.SelectFilter)
									continueButton.textContent = continueButton.dataset.labelselectfilter;
								if (label === ContinueButtonLabel.SelectGenerated)
									continueButton.textContent = continueButton.dataset.labelselectgenerated;
							});
						};

						// Enable the filter clicks
						const enableFilters = () => {
							const aiFilters = expressQuerySelectorAll<HTMLElement>(
								popupEl,
								".technical-aifilter-filter:not(.p-ai-multiple-filters__container-forbidden)",
							);
							aiFilters.forEach((aiFilter) => {
								eventDisposables.add(
									expressEventListener(aiFilter, "click", () => {
										// Check if we already clicked this filter before. If yes? Select it from the history
										const existingHistory = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-history[data-filterNumber='" +
												aiFilter.dataset.filternumber +
												"'][data-originalphotoid='" +
												activePhotoId +
												"']",
											false,
										);

										if (pageLog.dataset.filterNumber === aiFilter.dataset.filternumber) pageLogComponent.hideLog();

										if (existingHistory) {
											existingHistory.click();
											return;
										}

										expressAddClass(uploadButton, buttonDisabledClass);

										// Check if we have 0 attempts left, if so, block action (visual cue needed? Not yet)
										if (attemptsLeft === 0) return;

										hasCurrentCreations = true;

										// Set the filter as being active
										aiFilters.forEach((otherAiFilter) => {
											otherAiFilter.classList.remove(activeFilterClass);
										});
										aiFilter.classList.add(activeFilterClass);

										/* History photos */
										// History: Make a copy of the template
										const historyEl = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-history-template",
											false,
										);
										const newHistoryEl = historyEl.cloneNode(true) as HTMLImageElement;

										// History: Remove active class of other history items
										deselectActiveHistory();

										// History: Set attributes of the newly created history div
										newHistoryEl.dataset.filternumber = aiFilter.dataset.filternumber;
										newHistoryEl.classList.add(activeHistoryClass);
										newHistoryEl.classList.remove("u-hide");
										newHistoryEl.classList.remove("technical-aifilter-history-template");
										newHistoryEl.removeAttribute("data-photoid");
										newHistoryEl.removeAttribute("data-photourl");
										newHistoryEl.dataset.originalphotoid = activePhotoId || "";

										// History: Set the image to loading of the new history element
										const historyImage = expressQuerySelector<HTMLImageElement>(newHistoryEl, "img", true);
										historyImage.classList.add("u-hide");
										const loadingEl = expressQuerySelector<HTMLElement>(newHistoryEl, ".technical-photo-loading");
										loadingEl.classList.remove("u-hide");

										// History: Add the item to the DOM
										const historyContainer = historyEl.parentElement;
										if (historyContainer) historyContainer.insertBefore(newHistoryEl, historyContainer.firstChild);

										// History: Add a divider between past creations and current ones
										const divider = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-history-divider",
											false,
										);
										if (divider) divider.classList.remove("u-hide");

										continueButtonEls.forEach(
											(continueButton) => (continueButton.textContent = continueButton.dataset.labelcontinue),
										);

										fetch("/apicore/aifilter/starttask", {
											method: "POST",
											headers: {
												"Content-Type": "application/json",
											},
											body: JSON.stringify({
												originalPhotoId: activePhotoId,
												pvc,
												chosenFilterGroupNumber: aiFilter.dataset.filternumber,
											}),
										})
											.then((response) => response.json())
											.then((data) => {
												updatePendingLabels(data.label);
												const pendingId = data.pendingId;
												newHistoryEl.dataset.partnerid = pendingId;

												// Change the main picture to the original one
												mainPhoto.setAttribute("data-pendingid", pendingId);
												mainPhoto.src = activePhotoUrl;
												loading.classList.remove("u-hide");

												// error during start of task
												if (data.errorMessage) {
													showError(
														popupEl,
														pageLogComponent,
														pendingId,
														data.errorMessage,
														aiFilter.dataset.filternumber,
														data.attemptsLabel,
													);
													return;
												}
												// Disable the buttons
												continueButtonEls.forEach((continueButton) => {
													if (continueButton.href) {
														continueButton.classList.add(buttonDisabledClass);
														continueButton.setAttribute("data-href", continueButton.href);
														continueButton.removeAttribute("href");
													}
												});
												expressAddClass(uploadButton, buttonDisabledClass);

												return new Promise<void>((resolve, reject) => {
													pollForPending(popupEl, pageLogComponent, pendingId);
												});
											});
									}),
								);
							});
						};

						enableFilters();
						const updatePendingLabels = (attemptsText?: string) => {
							// Update the # attempts label
							if (attemptsText && attemptsLabelEl) {
								attemptsLabelEl.textContent = attemptsText;
								attemptsLabelEl.classList.remove("u-hide");
							}
						};

						const pendingId = mainPhoto.dataset.pendingid || "";
						if (pendingId && pendingId !== "") {
							return new Promise<void>((resolve, reject) => {
								pollForPending(popupEl, pageLogComponent, pendingId);
							});
						}
					}
				},
				forceRefresh: true, // refresh to reset api errors in popup
				data: {
					Pvc: pvc,
					Design: design,
				},
			}),
		);

		if (aiPhotoPopup.popup.dataset.hasError) {
			if (aiPhotoPopup.popup.dataset.showOnPagePagelog) {
				deps.error && deps.error(aiPhotoPopup.popup.dataset.errorMessage);
				return;
			}
			// something went wrong => open select photo dialog again
			return Promise.reject(aiPhotoPopup.popup.dataset.errorMessage);
		}

		await aiPhotoPopup.openAsync();

		const continueBtns = expressQuerySelectorAll<HTMLElement>(aiPhotoPopup.popup, ".technical-aifilter-continue");
		continueBtns.forEach((continueBtn) => {
			eventDisposables.add(
				expressEventListener(continueBtn, "click", () => {
					if (continueBtn.classList.contains(buttonDisabledClass)) return;
					showLoadingDialog(Promise.resolve(), 0, 10000);
				}),
			);
		});

		initDisableContextMenu(aiPhotoPopup.popup);
	};

	init();

	return {
		dispose: () => {
			eventDisposables.dispose();
		},
	};
}
